// A task card under an answer.
//   proposed          a fielding agent or a new study to confirm: the goal in
//                     numbers, what it will do on its own, Live or Shadow, and
//                     the numbers can be adjusted before confirming.
//   waiting_approval  an agent asks the person: the change, why, and its
//                     impact, with Approve / Decline right there.
//   anything else     one line with its state and a link to it.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { notification } from "antd";
import { toDisplayBrand } from "../../utils/displayBrand";
import { FIG_MUTED } from "../../design";
import {
  confirmOperatorTaskApi, stopOperatorTaskApi, approveOperatorTaskApi, rejectOperatorTaskApi, getOperatorTaskApi, getOperatorPlanApi,
} from "../../store/actions/operatorAction";
import { TestCallResult } from "./MoreCards";
import { QuestionnaireView } from "./QuestionnairePreview";
import ApproveConfirm from "./ApproveConfirm";
import { formatWhen } from "./time";
import { STATUS } from "./OperatorStyle";
import {
  Card, CardTop, Kicker, CardTitle, CardText, Section, SectionLabel, Tags, Tag, CardFoot,
  Primary, Secondary, TextButton, Receipt, SmallButton, Numbers, Change, Spinner,
} from "./CardStyle";
import { Fields, Field } from "./TaskStyle";

// A question's media as the card names it.
const mediaName = (m) => (m.media_type === "link" ? "a video link" : `the ${m.media_type} "${m.label || m.filename || "file"}"`);

export const TASK_STATUS = {
  proposed: { ...STATUS.waiting, label: "Needs your confirmation" },
  queued: { ...STATUS.running, label: "Scheduled" },
  running: { ...STATUS.running, label: "Checking now" },
  waiting_approval: { ...STATUS.waiting, label: "Waiting for your approval" },
  paused: { ...STATUS.queued, label: "Paused" },
  done: { ...STATUS.done, label: "Done" },
  stopped: { ...STATUS.queued, label: "Stopped" },
  failed: { ...STATUS.failed, label: "Failed" },
};
export const TEMPLATE_LABEL = { plan: "Plan", scheduled_action: "Scheduled action", fielding_agent: "Fielding Agent", field_new_study: "Field new study", watchdog: "Watchdog" };

/** A fielding agent's state in words: a live, watched study is "Fielding",
 * not "Scheduled". */
export const taskStatusLabel = (task) => {
  const fielding = ["field_new_study", "fielding_agent"].includes(task.template);
  if (fielding && task.status === "queued") return task.scope?.study_id ? "Fielding" : "Launching";
  if (fielding && task.status === "running") return task.scope?.study_id ? "Checking now" : "Launching";
  return (TASK_STATUS[task.status] || TASK_STATUS.proposed).label;
};

/** Who the panel will reach, in words: "US, ages 18 to 29, full time or part time". */
const words = (v) => String(v).replace(/_/g, " ");
const orList = (vals) => (vals.length > 1 ? `${vals.slice(0, -1).map(words).join(", ")} or ${words(vals[vals.length - 1])}` : words(vals[0]));
export const audienceText = (cfg = {}) => {
  if (cfg.launch_check?.audience) return cfg.launch_check.audience;
  const t = cfg.targeting || {};
  const parts = [(cfg.geography && cfg.geography.country) || "US"];
  if (t.age_min != null && t.age_max != null) parts.push(`ages ${t.age_min} to ${t.age_max}`);
  const title = (v) => words(v).replace(/\b\w/g, (c) => c.toUpperCase());
  if ((t.state || []).length) parts[0] = `${t.state.map(title).join(", ")}, ${parts[0]}`;
  for (const [field, label] of [["gender", ""], ["employment", "working "], ["education", "education: "], ["race", ""], ["hispanic_origin", ""], ["region", "region: "], ["division", "division: "]]) {
    if ((t[field] || []).length) parts.push(`${label}${orList(t[field])}`);
  }
  if ((t.children || []).length === 1) parts.push(t.children[0] === "have_children" ? "with children" : "without children");
  if (t.income_min != null && t.income_max != null) parts.push(`income $${Number(t.income_min).toLocaleString()} to $${Number(t.income_max).toLocaleString()}`);
  if (parts.length === 1) return cfg.audience_general ? `${parts[0]}, general population` : `${parts[0]}, no targeting set`;
  return parts.join(", ");
};

export const cleanTitle = (t) => toDisplayBrand(String(t || "").replace(/^field(?:\s+new\s+study)?\s*[:-]\s*/i, ""));
const money = (n) => (n == null || n === "" ? null : `$${Number(n).toLocaleString()}`);
const price = (n) => `$${Number(n).toFixed(2)}`;
const dateText = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : null);

/**
 * After "Confirm and launch": the launch as it happens (interviewer, study,
 * panel survey), then where it lives. Polls until the study exists or the
 * launch fails.
 */
function LaunchReceipt({ task: initial }) {
  const navigate = useNavigate();
  const [task, setTask] = useState(initial);
  const studyId = task.scope?.study_id || null;
  const failed = task.status === "failed";
  // Finished without a study: a simulated (shadow) launch, or a card stopped
  // or replaced. Nothing more will appear, so stop waiting and say so.
  const endedWithout = !studyId && !failed && ["done", "stopped"].includes(task.status);
  useEffect(() => { setTask(initial); }, [initial]);
  useEffect(() => {
    if (studyId || failed || endedWithout) return undefined;
    let alive = true;
    const iv = setInterval(async () => {
      try { const r = await getOperatorTaskApi(task._id); if (alive && r?.task) setTask(r.task); } catch (e) { /* keep waiting */ }
    }, 3000);
    return () => { alive = false; clearInterval(iv); };
  }, [task._id, studyId, failed, endedWithout]);
  const name = cleanTitle(task.study_config?.name || task.title);
  // The pre-launch test call's result, once the launch has run either way.
  const [testCall, setTestCall] = useState(null);
  const done = !!studyId || failed;
  useEffect(() => {
    if (!done || !task.study_config?.test_first || !task.plan) return;
    getOperatorPlanApi(task.plan).then((r) => {
      const plan = r?.plan || r;
      const step = (plan?.actions || []).find((a) => a.action === "agent.test");
      const res = step && plan.results && plan.results[step.id];
      if (res && res.result && res.result.test_call) setTestCall({ result: res.result, at: res.at });
    }).catch(() => {});
  }, [done, task.plan, task.study_config?.test_first]);
  const testCard = testCall ? <div style={{ marginTop: 8 }}><TestCallResult result={testCall.result} when={testCall.at ? formatWhen(testCall.at) : null} /></div> : null;
  if (failed) {
    return (
      <>
      <Receipt $tone="red">
        <span className="t"><b>The launch did not go through: {name}</b><span>{toDisplayBrand(task.stop_reason || "Open the agent to see which step stopped.")}</span></span>
        <span className="acts"><SmallButton type="button" onClick={() => navigate(`/operator/fielding/${task._id}`)}>Open agent</SmallButton></span>
      </Receipt>
      {testCard}
      </>
    );
  }
  if (endedWithout) {
    // Simulated = confirmed and run in shadow mode; a stopped card never ran.
    const simulated = task.status === "done" && (task.policy?.mode === "shadow" || /simulat/i.test(task.stop_reason || ""));
    return (
      <Receipt>
        <span className="t">
          <b>{simulated ? `Simulated only: ${name}` : `Not launched: ${name}`}</b>
          <span>{simulated ? "This card was confirmed in shadow mode, so the launch was only simulated: no interviewer or study was created and nothing was spent. Ask Copilot to launch it for real." : /replaced by a newer/i.test(task.stop_reason || "") ? "This card was replaced before it was confirmed, so nothing was created or spent. Ask Copilot to set it up again." : toDisplayBrand(task.stop_reason || "This card was stopped before it launched.")}</span>
        </span>
        <span className="acts"><SmallButton type="button" onClick={() => navigate(`/operator/fielding/${task._id}`)}>Open agent</SmallButton></span>
      </Receipt>
    );
  }
  if (!studyId) {
    return (
      <Receipt>
        <span className="t">
          <b><Spinner style={{ marginRight: 8, verticalAlign: "-2px" }} />Launching {name}</b>
          <span>{task.study_config?.modality === "web_form" ? "Creating the study, freezing its questionnaire and opening it for answers. This takes about a minute." : task.study_config?.test_first ? "Creating the interviewer and testing it with a simulated call; the study is created and published only if it passes. This takes a couple of minutes." : "Creating the interviewer and the study, then publishing it to the panel. This takes about a minute."}</span>
        </span>
      </Receipt>
    );
  }
  return (
    <>
    <Receipt $tone="green">
      <span className="t">
        <b>Live on the panel: {name}</b>
        <span>The fielding agent watches it{task.schedule?.next_run_at ? ` and checks next ${formatWhen(task.schedule.next_run_at)}` : ""}.{task.study_config?.library ? ` Good interviews go to the library "${toDisplayBrand(task.study_config.library.name || task.study_config.library.new_name)}" every night.` : " No library linked; you can add one from the study page."}</span>
      </span>
      <span className="acts">
        <SmallButton type="button" onClick={() => navigate(`/campaigns/${studyId}`)}>Open study</SmallButton>
        <SmallButton type="button" onClick={() => navigate(`/operator/fielding/${task._id}`)}>Open agent</SmallButton>
      </span>
    </Receipt>
    {testCard}
    </>
  );
}

/** An agent waiting on the person: the change it wants, why, and the impact. */
function DecisionCard({ task, onUpdated, onAsk }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  const plan = task.pending_plan || null;
  const p = task.progress || {};
  const cpiStep = plan && plan.steps.find((st) => st.action === "sourcing.update_cpi" && st.params && st.params.cpi != null);
  const decide = async (kind) => {
    setBusy(kind);
    try { onUpdated?.(await (kind === "approve" ? approveOperatorTaskApi(task._id) : rejectOperatorTaskApi(task._id))); } catch (e) {
      notification.error({ message: e?.response?.data?.message || "That did not work." });
    } finally { setBusy(null); }
  };
  const nums = [
    p.good != null && p.good_target ? { b: `${p.good} of ${p.good_target}`, s: "good interviews" } : null,
    p.good_per_day != null ? { b: `${Math.round(p.good_per_day * 10) / 10}/day`, s: p.needed_per_day ? `needs ${Math.round(p.needed_per_day * 10) / 10}/day` : "current pace" } : null,
    p.spend_usd != null ? { b: money(p.spend_usd), s: task.target?.panel_usd ? `of ${money(task.target.panel_usd)} cap` : "spent so far" } : null,
  ].filter(Boolean);
  return (
    <Card>
      <CardTop>
        <div className="row"><Kicker $tone="amber">Your decision</Kicker><span className="meta">Waiting since {formatWhen(task.updatedAt || task.updated_at)}</span></div>
        <p className="who">Fielding agent on {toDisplayBrand(task.study_name || cleanTitle(task.title))}</p>
        <CardTitle $big>{plan && plan.steps.length ? `${toDisplayBrand(plan.steps[0].text).replace(/\.$/, "")}?` : plan ? toDisplayBrand(plan.summary) : "Approve the agent's next step?"}</CardTitle>
        {cpiStep && p.price ? (
          <Change><span className="from">{price(p.price)}</span>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="#6D6A67" strokeWidth="1.6" aria-hidden="true"><path d="M3 9h12M11 5l4 4-4 4" /></svg>
            <span className="to">{price(cpiStep.params.cpi)}</span></Change>
        ) : null}
      </CardTop>
      {task.pending_reason || task.last_decision || (plan && plan.summary) ? (
        <Section><SectionLabel>Why it asks</SectionLabel><CardText style={{ marginTop: 0, whiteSpace: "pre-line" }}>{toDisplayBrand(task.pending_reason || task.last_decision || plan.summary)}</CardText></Section>
      ) : null}
      {plan && plan.steps.length > 1 ? (
        <Section><SectionLabel>What it would do</SectionLabel>{plan.steps.map((st, i) => <CardText key={i} style={{ marginTop: i ? 4 : 0 }}>{i + 1}. {toDisplayBrand(st.text)}</CardText>)}</Section>
      ) : null}
      {nums.length ? <Section><Numbers $cols={nums.length}>{nums.map((n) => <div key={n.s}><b>{n.b}</b><span>{n.s}</span></div>)}</Numbers></Section> : null}
      {plan && (plan.consequences || []).length ? <Section><Tags>{plan.consequences.map((t) => <Tag key={t.key} $tone={t.tone}>{t.label}</Tag>)}</Tags></Section> : null}
      <CardFoot>
        <ApproveConfirm change={plan && plan.steps.length ? plan.steps.map((st) => st.text).join(" ") : null} live={task.applies_live} onConfirm={() => decide("approve")}>
          <Primary type="button" disabled={!!busy}>{busy === "approve" ? "Approving…" : cpiStep ? `Approve ${price(cpiStep.params.cpi)}` : "Approve"}</Primary>
        </ApproveConfirm>
        <Secondary type="button" disabled={!!busy} onClick={() => decide("reject")}>{busy === "reject" ? "…" : "Decline"}</Secondary>
        {onAsk ? <TextButton type="button" $push onClick={() => onAsk(`Why does the fielding agent on ${task.study_name || cleanTitle(task.title)} want this, and what happens if I decline?`)}>Ask why</TextButton>
          : <TextButton type="button" $push onClick={() => navigate(`/operator/fielding/${task._id}`)}>Open the agent</TextButton>}
      </CardFoot>
    </Card>
  );
}

const TaskCard = ({ task, onUpdated, embedded = false, onAsk }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  const [adjust, setAdjust] = useState(false);
  // Choices made on the card survive it being rebuilt (the card is redrawn
  // when Copilot's answer finishes): kept per task for this browser tab.
  const draftKey = `copilot-card-${task._id}`;
  const saved = (() => { try { return JSON.parse(sessionStorage.getItem(draftKey) || "null"); } catch (e) { return null; } })();
  const [edits, setEditsState] = useState(() => ({
    good_completes: task.target?.good_completes ?? "",
    deadline: task.target?.deadline ? String(task.target.deadline).slice(0, 10) : "",
    panel_usd: task.target?.panel_usd ?? "",
    interval_minutes: task.schedule?.interval_minutes ?? 60,
    mode: task.policy?.mode || "shadow",
    // Where the interviews go: "p:<id>", "new" or "none" (optional).
    library: task.study_config?.library?.project_id ? `p:${task.study_config.library.project_id}` : task.study_config?.library?.new_name ? "new" : "none",
    test_first: task.study_config?.test_first === true,
    // "Ask for PIN" on the new interviewer (off: the call starts from the survey page).
    ask_pin: task.study_config?.agent_design ? task.study_config.agent_design.ask_pin !== false : true,
    // The new library's name: the suggestion, editable on the card.
    library_name: task.study_config?.library?.new_name || task.study_config?.library_options?.new_name || "",
    ...(saved || {}),
  }));
  const setEdits = (next) => {
    setEditsState(next);
    try { sessionStorage.setItem(draftKey, JSON.stringify(next)); } catch (e) { /* storage unavailable */ }
  };
  const st = TASK_STATUS[task.status] || TASK_STATUS.proposed;
  const ongoing = task.kind === "ongoing";
  const launch = task.template === "field_new_study";
  const fielding = launch || task.template === "fielding_agent";
  const cfg = task.study_config || {};
  const design = cfg.agent_design || null;
  const [allQs, setAllQs] = useState(false);
  // A questionnaire study shows the questionnaire's questions; a voice
  // interview the designed interviewer's.
  const quest = cfg.questionnaire || null;
  const questSurveyId = quest ? String(cfg.survey_id || quest.survey_id || "") || null : null;
  const isForm = cfg.modality === "web_form";
  const qs = quest ? (quest.questions || []).map((t) => ({ text: t })) : design ? design.questions || [] : [];
  const estSpend = cfg.responses && cfg.cpi ? Math.round(cfg.responses * cfg.cpi) : null;
  const check = cfg.launch_check || {};
  const libOpts = cfg.library_options || null;
  const chosenLib = cfg.library && cfg.library.project_id && !(libOpts?.matches || []).some((m) => String(m.project_id) === String(cfg.library.project_id)) ? cfg.library : null;
  const libChoices = libOpts ? [
    ...(chosenLib ? [{ key: `p:${chosenLib.project_id}`, label: chosenLib.name }] : []),
    ...(libOpts.matches || []).map((m) => ({ key: `p:${m.project_id}`, label: m.name, hint: m.files != null ? `${Number(m.files).toLocaleString()} ${m.files === 1 ? "interview" : "interviews"}` : null })),
    ...(libOpts.can_create && libOpts.new_name ? [{ key: "new", label: "New library", editable: true }] : []),
    { key: "none", label: "No library for now" },
  ] : [];
  const changed = [...(check.corrected || []), ...(check.overridden || [])];
  const advice = check.advice || [];
  const open = () => navigate(fielding ? `/operator/fielding/${task._id}` : `/operator/tasks/${task._id}`);

  if (task.status === "waiting_approval") return <DecisionCard task={task} onUpdated={onUpdated} onAsk={onAsk} />;

  if (launch && ["queued", "running", "failed"].includes(task.status) && !(task.runs_count > 1)) return <LaunchReceipt task={task} />;
  // A launch card that ended without a study (simulated, or stopped before
  // launching) says what happened instead of shrinking to a status line.
  if (launch && ["done", "stopped"].includes(task.status) && !task.scope?.study_id && !(task.runs_count > 1)) return <LaunchReceipt task={task} />;

  if (task.status !== "proposed") {
    const tone = { running: "blue", queued: "blue", done: "green", failed: "red" }[task.status];
    return (
      <Receipt $tone={tone === "green" ? "green" : tone === "red" ? "red" : undefined}>
        <span className="t">
          <b>{TEMPLATE_LABEL[task.template] || "Task"}: {cleanTitle(task.title)}</b>
          <span>{taskStatusLabel(task)}{task.last_run_at ? ` · last check ${formatWhen(task.last_run_at)}` : ""}{task.schedule?.next_run_at && ["queued", "paused"].includes(task.status) ? ` · next ${formatWhen(task.schedule.next_run_at)}` : ""}</span>
        </span>
        <span className="acts"><SmallButton type="button" onClick={open}>{fielding ? "Open agent" : "Open task"}</SmallButton></span>
      </Receipt>
    );
  }

  const confirm = async () => {
    setBusy("confirm");
    try {
      const body = {
        target: {
          good_completes: edits.good_completes === "" ? null : Number(edits.good_completes),
          deadline: edits.deadline || null,
          panel_usd: edits.panel_usd === "" ? null : Number(edits.panel_usd),
        },
        policy: { mode: edits.mode },
        interval_minutes: Number(edits.interval_minutes) || 60,
        ...(launch && design && edits.ask_pin !== (design.ask_pin !== false) ? { ask_pin: edits.ask_pin } : {}),
        ...(launch ? { test_first: edits.test_first, library: edits.library.startsWith("p:") ? { project_id: edits.library.slice(2) } : edits.library === "new" ? { new_name: String(edits.library_name || "").trim() || libOpts.new_name || cfg.name } : null } : {}),
      };
      onUpdated?.(await confirmOperatorTaskApi(task._id, body));
    } catch (e) {
      notification.error({ message: e?.response?.data?.message || "The task could not be started." });
    } finally {
      setBusy(null);
    }
  };
  const decline = async () => {
    setBusy("decline");
    try { onUpdated?.(await stopOperatorTaskApi(task._id)); } catch (e) {
      notification.error({ message: e?.response?.data?.message || "That did not work." });
    } finally { setBusy(null); }
  };

  const live = edits.mode === "live";
  const nums = fielding ? [
    edits.good_completes !== "" ? { b: Number(edits.good_completes).toLocaleString(), s: "good interviews" } : null,
    edits.deadline ? { b: dateText(edits.deadline), s: "deadline" } : null,
    edits.panel_usd !== "" ? { b: money(edits.panel_usd), s: "spend cap" } : null,
    cfg.cpi ? { b: price(cfg.cpi), s: cfg.cpi_ceiling ? `to start, up to ${price(cfg.cpi_ceiling)}` : "per interview" } : null,
  ].filter(Boolean) : [];
  const tags = launch
    ? (live ? [["Spends money", "amber"], ["Reaches panel respondents", "amber"], ["Stop any time", "grey"]] : [["Simulation: nothing is created or spent", "grey"]])
    : fielding ? (live ? [["Adjusts price and pacing on its own", "amber"], ["Asks before anything else", "grey"], ["Stop any time", "grey"]] : [["Recommends only", "grey"], ["Stop any time", "grey"]])
    : [["Runs on its own after you confirm", "grey"]];

  return (
    <Card>
      {embedded ? null : (
        <CardTop>
          <div className="row"><Kicker $tone="amber">Needs your confirmation</Kicker><span className="meta">{TEMPLATE_LABEL[task.template] || "Task"}</span></div>
          <CardTitle $big>{launch ? "Launch and watch: " : fielding ? "Watch: " : ""}{cleanTitle(task.title)}</CardTitle>
          {task.goal ? <CardText>{toDisplayBrand(task.goal)}</CardText> : null}
        </CardTop>
      )}
      {nums.length ? <Section><Numbers $cols={nums.length > 2 ? 2 : nums.length}>{nums.map((n) => <div key={n.s}><b>{n.b}</b><span>{n.s}</span></div>)}</Numbers></Section> : null}
      {launch ? (
        <Section>
          <SectionLabel>The study</SectionLabel>
          <CardText style={{ marginTop: 0 }}><b>Audience:</b> {audienceText(cfg)}</CardText>
          {design?.screen_out ? <CardText style={{ marginTop: 4 }}><b>Screened in the interview:</b> only {toDisplayBrand(design.screen_out)}; anyone else is thanked and the call ends.</CardText> : null}
          {quest ? (
            <CardText style={{ marginTop: 4 }}>
              <b>Survey:</b> {toDisplayBrand(quest.title)}, {quest.question_count} questions ({quest.closed} closed){cfg.survey_duration ? `, about ${cfg.survey_duration} min` : ""}
              {isForm ? ", answered on a web form" : cfg.modality === "respondent_choice" ? ", each person picks voice or web form" : `, asked by voice and the closed answers coded after each call, voice ${design?.voice_name || "John Della Volpe (default)"}`}
            </CardText>
          ) : (
            <CardText style={{ marginTop: 4 }}><b>Survey:</b> {design ? `${qs.length} questions` : "existing interview agent"}{cfg.survey_duration ? `, about ${cfg.survey_duration} min` : ""}{design ? `, voice ${design.voice_name || "John Della Volpe (default)"}` : ""}</CardText>
          )}
          {questSurveyId ? <QuestionnaireView surveyId={questSurveyId} label="The questionnaire" /> : null}
          {!questSurveyId && qs.length ? (allQs ? qs : qs.slice(0, 3)).map((q, i) => (
            <CardText key={i} style={{ marginTop: 3 }}>
              {i + 1}. {toDisplayBrand(q.text)}
              {q.media && q.media.media_type ? <span style={{ color: FIG_MUTED }}> · shows {mediaName(q.media)}</span> : null}
            </CardText>
          )) : null}
          {!questSurveyId && qs.length > 3 ? <TextButton type="button" onClick={() => setAllQs((v) => !v)}>{allQs ? "Show fewer" : `Show all ${qs.length} questions`}</TextButton> : null}
          {!questSurveyId && qs.some((q) => q.media && q.media.media_type) ? (
            <CardText style={{ marginTop: 6 }}><b>Media:</b> {qs.filter((q) => q.media && q.media.media_type).map((q) => `question ${qs.indexOf(q) + 1} shows ${mediaName(q.media)}`).join("; ")}. It shows on screen in the web call and the web form; only someone who dials in by phone misses it, and the interviewer cannot see it.</CardText>
          ) : null}
          {design ? (
            <CardText style={{ marginTop: 6 }}>
              <b>Calls:</b> {edits.ask_pin ? "the interviewer asks for the PIN shown on the survey page" : "no PIN: people start the call from the survey page in their browser"}.
              {cfg.pin_switch_allowed ? (
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6, marginLeft: 8, cursor: "pointer", color: "#4E4B48" }}>
                  <input type="checkbox" checked={edits.ask_pin} onChange={(e) => setEdits({ ...edits, ask_pin: e.target.checked })} />
                  Ask for a PIN
                </label>
              ) : null}
              {cfg.pin_note ? <span style={{ display: "block", color: "#6D6A67", marginTop: 2 }}>{toDisplayBrand(cfg.pin_note)}</span> : null}
            </CardText>
          ) : null}
          <CardText style={{ marginTop: 6 }}><b>Fielding:</b> {cfg.responses ? `${cfg.responses} interviews` : ""}{estSpend ? `, about ${money(estSpend)}` : ""}{cfg.duration ? `, ${cfg.duration} days` : ""}{cfg.qualifying_rate ? `, ${cfg.qualifying_rate}% qualifying` : ""}.</CardText>
          {(check.quotas || []).length ? <CardText style={{ marginTop: 4 }}><b>Quotas:</b> {check.quotas.join("; ")}</CardText> : null}
        </Section>
      ) : null}
      {launch && edits.mode === "live" && !isForm ? (
        <Section>
          <SectionLabel>Before launch (optional)</SectionLabel>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: "#2C2926", cursor: "pointer", lineHeight: 1.45 }}>
            <input type="checkbox" checked={edits.test_first} onChange={(e) => setEdits({ ...edits, test_first: e.target.checked })} style={{ marginTop: 3 }} />
            <span>Test the interviewer first with a simulated call. The study is created and launched only if it passes.<span style={{ display: "block", color: "#6D6A67", fontSize: 12 }}>About {Math.max(1, (qs.length || 6)) * 130} voice credits and a minute or two. No real people or panel involved.</span></span>
          </label>
        </Section>
      ) : null}
      {launch && libChoices.length ? (
        <Section>
          <SectionLabel>Library (optional)</SectionLabel>
          <CardText style={{ marginTop: 0 }}>Good interviews can go to a library every night, for chat, dashboards and reports. You can also link one later from the study page.</CardText>
          <div role="radiogroup" aria-label="Library" style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
            {libChoices.map((o) => (
              <label key={o.key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#2C2926", cursor: "pointer" }}>
                <input type="radio" name={`lib-${task._id}`} checked={edits.library === o.key} onChange={() => setEdits({ ...edits, library: o.key })} />
                {o.editable ? (
                  <span style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                    <span style={{ flex: "none" }}>New library:</span>
                    <input
                      type="text"
                      aria-label="New library name"
                      value={edits.library_name}
                      maxLength={120}
                      onFocus={() => { if (edits.library !== "new") setEdits({ ...edits, library: "new" }); }}
                      onChange={(e) => setEdits({ ...edits, library: "new", library_name: e.target.value })}
                      style={{ flex: 1, minWidth: 0, border: "1px solid #DCDAD7", borderRadius: 8, padding: "5px 8px", fontSize: 13, fontFamily: "inherit", color: "#2C2926" }}
                    />
                  </span>
                ) : (
                  <span>{toDisplayBrand(o.label)}{o.hint ? <span style={{ color: "#6D6A67" }}> · {o.hint}</span> : null}</span>
                )}
              </label>
            ))}
          </div>
        </Section>
      ) : null}
      {changed.length || advice.length ? (
        <Section>
          <SectionLabel>Checked against the panel rules</SectionLabel>
          {changed.map((l, i) => <CardText key={`c${i}`} style={{ marginTop: i ? 4 : 0 }}>Changed: {toDisplayBrand(l)}</CardText>)}
          {advice.map((l, i) => <CardText key={`a${i}`} style={{ marginTop: 4 }}>Advice: {toDisplayBrand(l)}</CardText>)}
        </Section>
      ) : null}
      {task.template !== "watchdog" ? (
        <Section>
          <div role="group" aria-label="Mode" style={{ display: "flex", border: "1px solid #DCDAD8", borderRadius: 10, overflow: "hidden" }}>
            {[["live", launch ? "Live: launch now" : "Live: adjusts within caps"], ["shadow", launch ? "Shadow: simulate first" : "Shadow: recommends only"]].map(([v, l]) => (
              <button key={v} type="button" aria-pressed={edits.mode === v} onClick={() => setEdits({ ...edits, mode: v })}
                style={{ flex: 1, border: 0, padding: "8px 6px", minHeight: 36, fontFamily: "inherit", fontSize: 12, fontWeight: 600, cursor: "pointer",
                  background: edits.mode === v ? "#EEF0FC" : "#fff", color: edits.mode === v ? "#2E45DA" : "#4E4B48" }}>{l}</button>
            ))}
          </div>
          <CardText>{launch ? (live ? "Confirming creates and launches the study now; spend starts, within your cap. The agent then adjusts price and pacing and asks you before anything else." : "Confirming simulates the launch; nothing is created or spent.")
            : ongoing ? `Runs on its own after you confirm and shows in ${fielding ? "Fielding Agents" : "Tasks"}.` : "Runs once at the scheduled time."}</CardText>
        </Section>
      ) : null}
      {adjust ? (
        <Fields>
          {fielding ? (
            <>
              <Field>Good interviews to reach<input type="number" min="1" value={edits.good_completes} onChange={(e) => setEdits({ ...edits, good_completes: e.target.value })} /></Field>
              <Field>By<input type="date" value={edits.deadline} onChange={(e) => setEdits({ ...edits, deadline: e.target.value })} /></Field>
              <Field>Panel spend cap ($)<input type="number" min="0" value={edits.panel_usd} onChange={(e) => setEdits({ ...edits, panel_usd: e.target.value })} /></Field>
            </>
          ) : null}
          {ongoing ? <Field>Check every (minutes)<input type="number" min="15" step="15" value={edits.interval_minutes} onChange={(e) => setEdits({ ...edits, interval_minutes: e.target.value })} /></Field> : null}
        </Fields>
      ) : null}
      <Section><Tags>{tags.map(([l, t]) => <Tag key={l} $tone={t}>{l}</Tag>)}</Tags></Section>
      <CardFoot>
        <Primary type="button" disabled={!!busy} onClick={confirm}>{busy === "confirm" ? "Starting…" : launch ? (live ? "Confirm and launch" : "Confirm (shadow)") : "Confirm and start"}</Primary>
        {fielding || ongoing ? <Secondary type="button" onClick={() => setAdjust((v) => !v)}>{adjust ? "Done adjusting" : "Adjust"}</Secondary> : null}
        <TextButton type="button" $muted $push disabled={!!busy} onClick={decline}>Not now</TextButton>
      </CardFoot>
    </Card>
  );
};

export default TaskCard;
